import React, { useState, useEffect } from 'react';
    import { motion } from 'framer-motion';
    import { useData } from '../context/DataContext';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiCreditCard, FiCalendar, FiDollarSign, FiCheck, FiDownload, FiFilter, FiSearch, FiUser } = FiIcons;

    const TransactionHistory = ({ studentId, parentId }) => {
      const { students, getParentById, formatCurrency, transactions } = useData();
      const [filteredTransactions, setFilteredTransactions] = useState([]);
      const [searchTerm, setSearchTerm] = useState('');
      const [statusFilter, setStatusFilter] = useState('all');
      const [dateRange, setDateRange] = useState({ start: '', end: '' });

      useEffect(() => {
        const enhancedTransactions = transactions.map(transaction => {
          const student = students.find(s => s.id === transaction.studentId);
          const parent = student ? getParentById(student.parentId) : null;
          
          // Safe name formatting to prevent crashes
          const pFirstName = parent?.firstName || '';
          const pLastName = parent?.lastName || '';
          const fullName = `${pFirstName} ${pLastName}`.trim();

          return {
            ...transaction,
            studentName: transaction.studentName || student?.studentName || 'Unknown Student',
            parentName: transaction.parentName || (fullName || 'Unknown Parent'),
            subject: student?.subject || 'General',
            tutor: student?.tutor || 'Tutor'
          };
        });

        let currentTransactions = [...enhancedTransactions];

        if (parentId) {
          const childrenIds = students.filter(s => s.parentId === parentId).map(s => s.id);
          currentTransactions = currentTransactions.filter(t => childrenIds.includes(t.studentId));
        }

        if (studentId) {
          currentTransactions = currentTransactions.filter(t => t.studentId === studentId);
        }

        if (searchTerm) {
          const lowercasedTerm = searchTerm.toLowerCase();
          currentTransactions = currentTransactions.filter(t => 
            (t.id?.toLowerCase().includes(lowercasedTerm)) || 
            (t.studentName?.toLowerCase().includes(lowercasedTerm)) || 
            (t.parentName?.toLowerCase().includes(lowercasedTerm))
          );
        }

        if (statusFilter !== 'all') {
          currentTransactions = currentTransactions.filter(t => t.status === statusFilter);
        }

        if (dateRange.start) {
          currentTransactions = currentTransactions.filter(t => new Date(t.timestamp) >= new Date(dateRange.start));
        }

        if (dateRange.end) {
          currentTransactions = currentTransactions.filter(t => new Date(t.timestamp) <= new Date(dateRange.end + 'T23:59:59'));
        }

        setFilteredTransactions(currentTransactions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
      }, [transactions, students, getParentById, studentId, parentId, searchTerm, statusFilter, dateRange]);

      const getStatusColor = (status) => {
        const colors = { 'completed': 'bg-green-100 text-green-800', 'pending': 'bg-yellow-100 text-yellow-800', 'failed': 'bg-red-100 text-red-800' };
        return colors[status] || 'bg-gray-100 text-gray-800';
      };

      return (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mt-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTransactions.map((tx) => (
                  <tr key={tx.id}>
                    <td className="px-6 py-4 text-sm text-gray-900">{new Date(tx.timestamp).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{tx.studentName}</td>
                    <td className="px-6 py-4 text-sm font-bold text-gray-900">{formatCurrency(tx.amount)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(tx.status)}`}>
                        {tx.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    };

    export default TransactionHistory;